import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import {
  useChampionTheme, TrophyIcon, GoldButton, ChampionBadge,
} from '../../components/ChampionElements';

export default function EpisodeView() {
  const { webinarId, epId } = useParams();
  const navigate = useNavigate();
  const [episode, setEpisode] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const t = useChampionTheme();
  const podiumColors = [t.gold, t.silver, t.bronze];

  useEffect(() => {
    setEpisode(null);
    setLoading(true);
    window.scrollTo(0, 0);
    const fetchData = async () => {
      try {
        const [epRes, listRes] = await Promise.all([
          api.get(`/api/member/webinars/${webinarId}/episodes/${epId}`),
          api.get(`/api/member/webinars/${webinarId}/episodes`),
        ]);
        setEpisode(epRes.data);
        setEpisodes(listRes.data);
      } catch (err) {
        alert('Gagal memuat episode.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [webinarId, epId]);

  const handleMarkComplete = async () => {
    setMarking(true);
    try {
      await api.post(`/api/member/episodes/${epId}/complete`);
      setEpisode(prev => ({ ...prev, is_completed: true }));
      setEpisodes(prev => prev.map(ep => ep.id === Number(epId) ? { ...ep, is_completed: true } : ep));
      alert('Episode ditandai selesai!');
    } catch (err) {
      alert('Gagal menandai selesai.');
    } finally {
      setMarking(false);
    }
  };

  const currentIndex = episodes.findIndex(ep => ep.id === Number(epId));
  const prevEpisode = currentIndex > 0 ? episodes[currentIndex - 1] : null;
  const nextEpisode = currentIndex < episodes.length - 1 ? episodes[currentIndex + 1] : null;

  if (loading) {
    return <div className="text-center py-5"><TrophyIcon size={48} /><p className="mt-3" style={{ color: t.gold }}>Memuat episode...</p></div>;
  }

  if (!episode) {
    return <div className="alert" style={{ backgroundColor: t.errorBg, color: t.error, borderRadius: '12px' }}>Episode tidak ditemukan.</div>;
  }

  return (
    <div>
      {/* Top Navigation */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <button className="btn btn-sm" style={{
          background: t.elementBg, border: `1px solid ${t.borderLight}`, color: t.textMuted,
          borderRadius: '10px', padding: '8px 16px', fontWeight: 500,
        }} onClick={() => navigate(`/learning/${webinarId}/episodes`)}>
          <i className="bi bi-arrow-left me-1"></i>Daftar Episode
        </button>
        <div className="d-flex gap-2">
          {prevEpisode && (
            <button className="btn btn-sm" style={{
              background: 'transparent', border: `1.5px solid ${t.gold}`, color: t.gold,
              borderRadius: '10px', padding: '8px 16px', fontWeight: 600,
            }} onClick={() => navigate(`/learning/${webinarId}/episodes/${prevEpisode.id}`)}>
              <i className="bi bi-chevron-left me-1"></i>Sebelumnya
            </button>
          )}
          {nextEpisode && (
            <GoldButton onClick={() => navigate(`/learning/${webinarId}/episodes/${nextEpisode.id}`)} style={{ padding: '8px 18px', fontSize: '0.85rem' }}>
              Selanjutnya<i className="bi bi-chevron-right ms-1"></i>
            </GoldButton>
          )}
        </div>
      </div>

      {/* Episode Content */}
      <div style={{ animation: 'slide-in-up 0.4s ease' }}>
        <div className="d-flex align-items-center gap-2 mb-2">
          <i className="bi bi-play-circle-fill" style={{ color: t.gold, fontSize: '1.3rem' }}></i>
          <h5 style={{ color: t.text, fontWeight: 700, margin: 0 }}>{episode.title}</h5>
          {episode.is_completed && <ChampionBadge color={t.success}><i className="bi bi-check-circle me-1"></i>Selesai</ChampionBadge>}
        </div>
        {episode.description && <p style={{ color: t.textMuted, fontSize: '0.9rem' }}>{episode.description}</p>}

        {episode.video_url && (
          <div className="ratio ratio-16x9 mb-4" style={{ borderRadius: '12px', overflow: 'hidden', border: `1px solid ${t.border}` }}>
            <iframe src={episode.video_url} title={episode.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mb-4">
          {episode.duration && <ChampionBadge color={t.textMuted}><i className="bi bi-clock me-1"></i>{episode.duration} menit</ChampionBadge>}
          {!episode.is_completed ? (
            <button className="btn" onClick={handleMarkComplete} disabled={marking} style={{
              background: `linear-gradient(135deg, ${t.success}, #16A34A)`, border: 'none', color: '#fff',
              fontWeight: 700, borderRadius: '12px', padding: '10px 24px',
              boxShadow: '0 4px 15px rgba(34,197,94,0.3)',
            }}>
              {marking ? 'Menyimpan...' : <><i className="bi bi-check-lg me-1"></i>Tandai Selesai</>}
            </button>
          ) : (
            <span className="d-flex align-items-center gap-2" style={{ color: t.success, fontWeight: 700 }}>
              <i className="bi bi-trophy-fill" style={{ fontSize: '1.2rem' }}></i>Episode Selesai
            </span>
          )}
        </div>
      </div>

      {/* All Episodes List */}
      <div style={{ marginTop: '10px' }}>
        <div style={{ height: '2px', background: `linear-gradient(90deg, ${t.gold}00, ${t.gold}44, ${t.gold}00)`, marginBottom: '20px' }} />
        <div className="d-flex align-items-center gap-2 mb-3">
          <i className="bi bi-list-ul" style={{ color: t.gold }}></i>
          <h6 style={{ color: t.gold, fontWeight: 700, margin: 0 }}>Semua Episode</h6>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {episodes.map((ep, index) => {
            const isActive = ep.id === Number(epId);
            const badgeColor = index < 3 ? podiumColors[index] : t.incompleteBorder;
            return (
              <button key={ep.id} className="list-group-item d-flex align-items-center justify-content-between"
                onClick={() => { if (!isActive) navigate(`/learning/${webinarId}/episodes/${ep.id}`); }}
                style={{
                  cursor: isActive ? 'default' : 'pointer', textAlign: 'left',
                  background: isActive ? `${t.gold}12` : t.cardBg,
                  border: isActive ? `1px solid ${t.gold}44` : `1px solid ${t.borderLight}`,
                  borderRadius: '10px', padding: '12px 16px', color: t.text, transition: 'all 0.3s ease',
                }}>
                <div className="d-flex align-items-center">
                  {ep.is_completed ? (
                    <span className="badge rounded-circle me-3" style={{ width: '28px', height: '28px', lineHeight: '20px', background: `linear-gradient(135deg, ${t.success}, #16A34A)`, color: '#fff' }}>
                      <i className="bi bi-check"></i>
                    </span>
                  ) : (
                    <span className="badge rounded-circle me-3" style={{ width: '28px', height: '28px', lineHeight: '20px', background: isActive ? `${t.gold}44` : badgeColor, color: isActive ? t.gold : (index < 3 ? t.bg : t.textMuted), fontWeight: 700 }}>
                      {index + 1}
                    </span>
                  )}
                  <div>
                    <strong style={{ color: isActive ? t.gold : t.text }}>{ep.title}</strong>
                    {ep.duration && <small className="d-block" style={{ color: t.textMuted }}><i className="bi bi-clock me-1"></i>{ep.duration} menit</small>}
                  </div>
                </div>
                {isActive && <span style={{ color: t.gold, fontSize: '0.8rem', fontWeight: 600 }}><i className="bi bi-play-fill me-1"></i>Sedang ditonton</span>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
